/**
 * Controls progress and arrow steps for scroll-snap product sliders.
 * The optional data-inner-gallery-guard attribute isolates nested card galleries.
 */
(function () {
  // A colourway change can add new slider roots.
  if (window.scrollSnapSliderInitialized) {
    if (typeof window.scrollSnapSliderSetup === 'function') window.scrollSnapSliderSetup(document);
    return;
  }
  window.scrollSnapSliderInitialized = true;

  const resizeUpdates = new Set();
  let resizeListenerBound = false;

  function pruneResizeUpdates() {
    resizeUpdates.forEach(function (entry) {
      if (!entry.root.isConnected) resizeUpdates.delete(entry);
    });
  }

  function registerResizeUpdate(root, scheduleUpdate) {
    pruneResizeUpdates();
    resizeUpdates.add({ root, scheduleUpdate });
    if (resizeListenerBound) return;

    window.addEventListener('resize', function () {
      resizeUpdates.forEach(function (entry) {
        if (!entry.root.isConnected) {
          resizeUpdates.delete(entry);
          return;
        }
        entry.scheduleUpdate();
      });
    });
    resizeListenerBound = true;
  }

  function init(root) {
    if (root.dataset.scrollSnapSliderInit) return;

    const slideshow = root.querySelector('.shop-the-look-slider');
    const track = slideshow?.querySelector('slideshow-slides');
    const fill = root.querySelector('.grid-3-progress-bar-fill');
    const prevBtn = slideshow?.querySelector(':scope > slideshow-container > slideshow-arrows [ref="previous"]');
    const nextBtn = slideshow?.querySelector(':scope > slideshow-container > slideshow-arrows [ref="next"]');

    if (!track || !fill) return;

    const items = track.querySelectorAll('.grid-3-inr');
    if (items.length <= 1) return;
    root.dataset.scrollSnapSliderInit = 'true';

    function getIndex() {
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      const w = (items[0].getBoundingClientRect().width + gap) || (items[0].offsetWidth + gap);
      return w > 0 ? Math.round(track.scrollLeft / w) : 0;
    }

    function scrollStep(step) {
      const nextIndex = Math.max(0, Math.min(getIndex() + step, items.length - 1));
      items[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    function update() {
      if (!root.isConnected) return;

      const { scrollLeft, scrollWidth, clientWidth } = track;
      const maxScroll = scrollWidth - clientWidth;

      // Progress
      const slides = items.length;
      const base = 1 / slides;
      const scrollProgress = maxScroll > 0 ? Math.min(scrollLeft / maxScroll, 1) : 0;
      const progress = base + (scrollProgress * (1 - base));
      fill.style.setProperty('--scroll-progress', progress);

      // Arrows state — slideshow.js owns these too, this keeps them in sync with the scroll
      if (prevBtn && nextBtn) {
        prevBtn.disabled = scrollLeft <= 5;
        nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 5;
      }
    }

    // Measure the track only once in each frame.
    let frame = null;
    function scheduleUpdate() {
      if (frame !== null) return;
      frame = requestAnimationFrame(function () {
        frame = null;
        update();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        scrollStep(-1);
      }, true);
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        scrollStep(1);
      }, true);
    }

    // Capture nested gallery input before the outer slider receives it.
    if (root.hasAttribute('data-inner-gallery-guard')) {
      ['click', 'mousedown', 'pointerdown', 'touchstart'].forEach((eventType) => {
        root.addEventListener(eventType, (e) => {
          const innerControl = e.target.closest('.product-card-images-slider slideshow-arrows, .product-card-images-slider .slideshow-control, .product-card-images-slider .pcs-block');
          if (innerControl) {
            e.stopPropagation();
            if (eventType === 'click') {
              const innerSlideshow = innerControl.closest('slideshow-component');
              const innerNext = e.target.closest('[ref="next"]');
              const innerPrev = e.target.closest('[ref="previous"]');

              if (innerSlideshow && typeof innerSlideshow.select === 'function') {
                const slidesCount = innerSlideshow.slides?.length || innerSlideshow.querySelectorAll('slideshow-slide').length || 0;
                if (innerNext && slidesCount > 0 && innerSlideshow.current >= slidesCount - 1) {
                  e.preventDefault();
                  e.stopImmediatePropagation();
                  innerSlideshow.select(0);
                  return;
                }
                if (innerPrev && slidesCount > 0 && innerSlideshow.current <= 0) {
                  e.preventDefault();
                  e.stopImmediatePropagation();
                  innerSlideshow.select(slidesCount - 1);
                  return;
                }
              }
            }
          }
        }, true);
      });
    }

    // Load nested gallery images when touch input starts.
    root.querySelectorAll('.product-card-images-slider').forEach((slider) => {
      slider.addEventListener('touchstart', () => {
        slider.setAttribute('actioned', '');
        slider.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
      }, { passive: true, once: true });
    });

    track.addEventListener('scroll', scheduleUpdate, { passive: true });
    registerResizeUpdate(root, scheduleUpdate);

    update();
    // The slideshow component can change the track size after it connects.
    setTimeout(function () {
      if (root.isConnected) update();
    }, 500);
  }

  // Nothing runs until the slider approaches the viewport — both roots sit below the fold.
  function observe(root) {
    if (root.dataset.lazyInitObserved) return;
    root.dataset.lazyInitObserved = 'true';

    if (!('IntersectionObserver' in window)) {
      init(root);
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        init(root);
        if (!root.dataset.scrollSnapSliderInit) delete root.dataset.lazyInitObserved;
      });
    }, { rootMargin: '200px 0px' });
    observer.observe(root);
  }

  function setup(scope) {
    pruneResizeUpdates();
    (scope || document).querySelectorAll('[data-scroll-snap-slider]').forEach(observe);
  }
  window.scrollSnapSliderSetup = setup;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setup(document);
    });
  } else {
    setup(document);
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      setup(event.target);
    });
  }
})();
